import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  FaEdit,
  FaDownload,
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaClock,
  FaMedal,
  FaBook,
  FaLock,
  FaBuilding,
} from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import { Country, State, City } from 'country-state-city';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function UserProfile() {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { data: userData, loading, error, fetchData } = useApi(`/users/${user?.id || ''}`);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] = useState(false);
  const [selectedProfilePicture, setSelectedProfilePicture] = useState(null);
  const [documentError, setDocumentError] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    address: '',
    phone: '',
    postalCode: '',
    street: '',
    country: '',
    state: '',
    city: '',
    profilePicture: null,
    fees: 0,
    availability: '',
    bio: '',
    specialties: [],
    experience: 0,
    timeSlots: [],
    userType: 'individual',
    companyName: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  useEffect(() => {
    if (userData) {
      setForm({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        role: userData.role || '',
        address: userData.address || '',
        phone: userData.phone || '',
        postalCode: userData.postalCode || '',
        street: userData.street || '',
        country: userData.country || '',
        state: userData.state || '',
        city: userData.city || '',
        profilePicture: null,
        fees: userData.fees || 0,
        availability: userData.availability || 'not_available',
        bio: userData.bio || '',
        specialties: userData.specialties || [],
        experience: userData.experience || 0,
        timeSlots: userData.timeSlots || [],
        userType: userData.userType || 'individual',
        companyName: userData.companyName || '',
      });
    }
  }, [userData]);

  if (!isAuthenticated()) {
    toast.error('Please log in to view your profile.');
    return <Navigate to="/login" />;
  }

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setForm({
      ...form,
      [name]: type === 'file' ? files[0] : value,
    });
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value,
    });
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleCountryChange = (selected) => {
    setForm({ ...form, country: selected?.value || '', state: '', city: '' });
  };

  const handleStateChange = (selected) => {
    setForm({ ...form, state: selected?.value || '', city: '' });
  };

  const handleCityChange = (selected) => {
    setForm({ ...form, city: selected?.value || '' });
  };

  const handleAvailabilityChange = (selected) => {
    setForm({ ...form, availability: selected?.value || '' });
  };

  const handleSpecialtyChange = (selected) => {
    if (selected.length > 5) {
      toast.error('Maximum 5 specialties allowed.');
      return;
    }
    setForm({ ...form, specialties: selected.map((opt) => opt.value) });
  };

  const handleTimeSlotsChange = (selected) => {
    setForm({ ...form, timeSlots: selected.map((opt) => opt.value) });
  };

  const handleUserTypeChange = (selected) => {
    const newType = selected?.value || 'individual';
    setForm({
      ...form,
      userType: newType,
      ...(newType === 'company' && { firstName: '', lastName: '' }),
      ...(newType === 'individual' && { companyName: '' }),
    });
  };

  const getDisplayName = () => {
    if (userData?.userType === 'company' && userData?.companyName) {
      return userData.companyName;
    }
    return `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'User';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      // Prepare name fields based on userType
      let submitFirstName = form.firstName;
      let submitLastName = form.lastName;

      if (form.role === 'user' && form.userType === 'company') {
        submitFirstName = form.companyName?.trim() || '';
        submitLastName = '';
        if (!submitFirstName) {
          toast.error('Company name is required');
          return;
        }
      } else if (form.role === 'user' && form.userType === 'individual') {
        if (!submitFirstName.trim() || !submitLastName.trim()) {
          toast.error('First name and last name are required');
          return;
        }
      }

      formData.append('firstName', submitFirstName);
      formData.append('lastName', submitLastName);
      formData.append('companyName', form.companyName || '');
      formData.append('userType', form.userType);

      formData.append('email', form.email);
      formData.append('address', form.address);
      formData.append('phone', form.phone);
      formData.append('postalCode', form.postalCode);
      formData.append('street', form.street);
      formData.append('country', form.country);
      formData.append('state', form.state);
      formData.append('city', form.city);

      if (form.profilePicture) formData.append('profilePicture', form.profilePicture);

      if (form.role === 'specialist') {
        formData.append('fees', form.fees);
        formData.append('availability', form.availability);
        formData.append('bio', form.bio);
        form.specialties.forEach((s, i) => formData.append(`specialties[${i}]`, s));
        formData.append('experience', form.experience);
        form.timeSlots.forEach((slot, i) => formData.append(`timeSlots[${i}]`, slot));
      }

      await apiService.upload(`/users/${user.id}`, formData, { method: 'PUT' });

      toast.success('Profile updated successfully');
      fetchData();
      setIsEditModalOpen(false);
      setForm((prev) => ({ ...prev, profilePicture: null }));
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    if (!passwordForm.currentPassword) {
      toast.error('Current password is required.');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long.');
      return;
    }

    try {
      await apiService.post(`/users/change-password`, {
        userId: user.id,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast.success('Password updated successfully');
      setIsPasswordModalOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update password';
      toast.error(errorMessage);
    }
  };

  const handleViewProfilePicture = async (userId, profilePicture) => {
    setSelectedProfilePicture({ userId, url: profilePicture });
    setDocumentError(null);
    try {
      const { blob, contentType } = await apiService.getBlob(`/users/document/${userId}/profilePicture`);
      if (!contentType.match(/image\/(jpeg|png)/)) {
        throw new Error('Unsupported profile picture format.');
      }
      const blobUrl = URL.createObjectURL(blob);
      setSelectedProfilePicture({ userId, blobUrl, contentType });
      setIsProfilePictureModalOpen(true);
    } catch (err) {
      setDocumentError(err.message || 'Failed to load profile picture');
      toast.error(err.message || 'Failed to load profile picture');
    }
  };

  const countryOptions = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  const stateOptions = form.country
    ? State.getStatesOfCountry(form.country).map((s) => ({
        value: s.isoCode,
        label: s.name,
      }))
    : [];

  const cityOptions = form.country && form.state
    ? City.getCitiesOfState(form.country, form.state).map((c) => ({
        value: c.name,
        label: c.name,
      }))
    : [];

  const availabilityOptions = [
    { value: 'available', label: 'Available' },
    { value: 'not_available', label: 'Not Available' },
  ];

  const specialtyOptions = [
    { value: 'poultry_care', label: 'Poultry Care' },
    { value: 'cattle_health', label: 'Cattle Health' },
    { value: 'veterinary_surgery', label: 'Veterinary Surgery' },
    { value: 'animal_nutrition', label: 'Animal Nutrition' },
    { value: 'livestock_management', label: 'Livestock Management' },
    { value: 'disease_control', label: 'Disease Control' },
    { value: 'farm_vaccination', label: 'Farm Vaccination' },
    { value: 'poultry_diagnostics', label: 'Poultry Diagnostics' },
    { value: 'avian_diseases', label: 'Avian Diseases Specialist' },
    { value: 'hatchery_management', label: 'Hatchery Management' },
    { value: 'broiler_management', label: 'Broiler Management' },
    { value: 'layer_management', label: 'Layer Farm Management' },
    { value: 'biosecurity_management', label: 'Biosecurity Management' },
    { value: 'feed_formulation', label: 'Feed Formulation' },
    { value: 'parasite_control', label: 'Parasite Control' },
    { value: 'antibiotic_stewardship', label: 'Antibiotic Stewardship' },
    { value: 'reproductive_health', label: 'Reproductive Health' },
    { value: 'egg_quality_management', label: 'Egg Quality Management' },
    { value: 'farm_sanitation', label: 'Farm Sanitation & Hygiene' },
    { value: 'environmental_management', label: 'Poultry Environment Management' },
    { value: 'heat_stress_management', label: 'Heat Stress Management' },
    { value: 'poultry_welfare', label: 'Animal Welfare (Poultry)' },
    { value: 'veterinary_consultation', label: 'General Veterinary Consultation' },
  ];

  const timeSlotOptions = [
    { value: '08:00-10:00', label: '08:00 AM - 10:00 AM' },
    { value: '10:00-12:00', label: '10:00 AM - 12:00 PM' },
    { value: '12:00-14:00', label: '12:00 PM - 02:00 PM' },
    { value: '14:00-16:00', label: '02:00 PM - 04:00 PM' },
    { value: '16:00-18:00', label: '04:00 PM - 06:00 PM' },
    { value: '18:00-20:00', label: '06:00 PM - 08:00 PM' },
  ];

  const userTypeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'company', label: 'Company' },
  ];

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-xl shadow-2xl min-h-screen">
      <div className="bg-gradient-to-r from-primary to-secondary p-6 rounded-xl mb-8 text-white transform hover:scale-105 transition-transform duration-300">
        <h1 className="text-4xl font-extrabold tracking-tight">Welcome to Your Dashboard, {getDisplayName()}!</h1>
        <p className="text-lg mt-2">Manage your profile and showcase your expertise.</p>
        <div className="mt-4 flex space-x-4">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="px-6 py-3 bg-white text-primary font-semibold rounded-lg shadow-md hover:bg-secondary hover:text-white transition-all duration-300 flex items-center gap-2"
          >
            <FaEdit /> Edit Profile
          </button>
          <button
            onClick={() => setIsPasswordModalOpen(true)}
            className="px-6 py-3 bg-white text-primary font-semibold rounded-lg shadow-md hover:bg-secondary hover:text-white transition-all duration-300 flex items-center gap-2"
          >
            <FaLock /> Change Password
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 dark:text-gray-400 animate-pulse">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500 dark:text-red-400 font-medium">Error: {error}</div>
      ) : !userData ? (
        <div className="text-center text-gray-500 dark:text-gray-400">No profile data found.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Picture Card */}
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg flex flex-col items-center transform hover:scale-105 transition-transform duration-300 border-4 border-primary dark:border-secondary">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
              <FaUserCircle /> Profile Picture
            </h3>
            {userData.profilePicture ? (
              <button onClick={() => handleViewProfilePicture(userData._id, userData.profilePicture)} className="group relative">
                <img
                  src={`${BASE_URL}${userData.profilePicture}`}
                  alt="Profile"
                  className="w-48 h-48 rounded-full object-cover border-4 border-primary dark:border-secondary shadow-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <span className="text-white font-medium bg-black bg-opacity-60 px-4 py-2 rounded">View</span>
                </div>
              </button>
            ) : (
              <div className="w-48 h-48 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xl font-medium">
                No Image
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">Click to view or download</p>
          </div>

          {/* Main Profile Details */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
              <FaEnvelope /> Profile Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaUserCircle className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Name</label>
                  <p className="text-lg text-gray-900 dark:text-white">{getDisplayName()}</p>
                </div>
              </div>

              {userData.userType === 'company' && (
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                  <FaBuilding className="text-primary dark:text-secondary" />
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Company Name</label>
                    <p className="text-lg text-gray-900 dark:text-white">{userData.companyName || 'N/A'}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaUserCircle className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Account Type</label>
                  <p className="text-lg text-gray-900 dark:text-white capitalize">
                    {userData.userType || 'individual'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaEnvelope className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Email</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaUserCircle className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Role</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.role || 'N/A'}</p>
                </div>
              </div>

              {/* Rest of the fields remain the same */}
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaPhone className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Phone</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.phone || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Country</label>
                  <p className="text-lg text-gray-900 dark:text-white">
                    {userData.country ? Country.getCountryByCode(userData.country)?.name || userData.country : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">State</label>
                  <p className="text-lg text-gray-900 dark:text-white">
                    {userData.state ? State.getStateByCodeAndCountry(userData.state, userData.country)?.name || userData.state : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">City</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.city || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Address</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.address || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Street</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.street || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                <FaMapMarkerAlt className="text-primary dark:text-secondary" />
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Postal Code</label>
                  <p className="text-lg text-gray-900 dark:text-white">{userData.postalCode || 'N/A'}</p>
                </div>
              </div>

              {userData.role === 'specialist' && (
                <>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaMoneyBillWave className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Fees (£)</label>
                      <p className="text-lg text-gray-900 dark:text-white">{userData.fees || '0'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaClock className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Availability</label>
                      <p className="text-lg text-gray-900 dark:text-white">
                        {userData.availability === 'available' ? 'Available' : 'Not Available'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaClock className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Time Slots</label>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {userData.timeSlots?.length > 0
                          ? userData.timeSlots
                              .map((slot) => timeSlotOptions.find((opt) => opt.value === slot)?.label || slot)
                              .join(', ')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaMedal className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Experience (Years)</label>
                      <p className="text-lg text-gray-900 dark:text-white">{userData.experience || '0'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaBook className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Specialties</label>
                      <p className="text-lg text-gray-900 dark:text-white">
                        {userData.specialties?.length > 0
                          ? userData.specialties
                              .map((s) => specialtyOptions.find((opt) => opt.value === s)?.label || s)
                              .join(', ')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg shadow-md hover:bg-secondary/20 dark:hover:bg-primary/20 transition-all duration-200">
                    <FaBook className="text-primary dark:text-secondary" />
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-300">Bio</label>
                      <p className="text-lg text-gray-900 dark:text-white">{userData.bio || 'N/A'}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setForm((prev) => ({ ...prev, profilePicture: null }));
        }}
        title="Edit Profile"
        className="max-w-5xl mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border-2 border-primary dark:border-secondary"
      >
        <div className="max-h-[80vh] overflow-y-auto p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-md border-2 border-primary/20 dark:border-secondary/20">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <FaUserCircle /> Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter email"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Role</label>
                  <p className="text-lg text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    {form.role || 'N/A'}
                  </p>
                </div>

                {form.role === 'user' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Account Type</label>
                    <Select
                      options={userTypeOptions}
                      value={userTypeOptions.find((opt) => opt.value === form.userType) || null}
                      onChange={handleUserTypeChange}
                      placeholder="Select account type"
                      className="w-full"
                      classNamePrefix="react-select"
                    />
                  </div>
                )}

                {form.role === 'user' && form.userType === 'company' ? (
                  <FormInput
                    label="Company Name"
                    name="companyName"
                    type="text"
                    value={form.companyName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter company name"
                    className="md:col-span-2"
                  />
                ) : (
                  <>
                    <FormInput
                      label="First Name"
                      name="firstName"
                      type="text"
                      value={form.firstName}
                      onChange={handleInputChange}
                      required={form.userType !== 'company'}
                      placeholder="Enter first name"
                    />
                    <FormInput
                      label="Last Name"
                      name="lastName"
                      type="text"
                      value={form.lastName}
                      onChange={handleInputChange}
                      required={form.userType !== 'company'}
                      placeholder="Enter last name"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Contact Information Section - remains the same */}
            <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-md border-2 border-primary/20 dark:border-secondary/20">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <FaMapMarkerAlt /> Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Country</label>
                  <Select
                    options={countryOptions}
                    value={countryOptions.find((opt) => opt.value === form.country) || null}
                    onChange={handleCountryChange}
                    placeholder="Select a country"
                    isClearable
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">State</label>
                  <Select
                    options={stateOptions}
                    value={stateOptions.find((opt) => opt.value === form.state) || null}
                    onChange={handleStateChange}
                    placeholder="Select a state"
                    isClearable
                    isDisabled={!form.country}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">City</label>
                  <Select
                    options={cityOptions}
                    value={cityOptions.find((opt) => opt.value === form.city) || null}
                    onChange={handleCityChange}
                    placeholder="Select a city (optional)"
                    isClearable
                    isDisabled={!form.state}
                  />
                </div>
                <FormInput label="Address" name="address" type="text" value={form.address} onChange={handleInputChange} required />
                <FormInput label="Street" name="street" type="text" value={form.street} onChange={handleInputChange} required />
                <FormInput label="Postal Code" name="postalCode" type="text" value={form.postalCode} onChange={handleInputChange} required />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Phone Number</label>
                  <PhoneInput
                    country={'us'}
                    value={form.phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter phone number"
                    inputClass="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white"
                    dropdownClass="border border-gray-300 dark:border-gray-600 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <FormInput
                    label="Profile Picture (JPG/PNG)"
                    name="profilePicture"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleInputChange}
                  />
                </div>
                {userData?.profilePicture && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Current Profile Picture</label>
                    <button onClick={() => handleViewProfilePicture(userData._id, userData.profilePicture)} className="mt-2">
                      <img
                        src={`${BASE_URL}${userData.profilePicture}`}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600 shadow-md"
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {form.role === 'specialist' && (
              <div className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-md border-2 border-primary/20 dark:border-secondary/20">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <FaMedal /> Professional Details
                </h3>
                {/* ... specialist fields remain unchanged ... */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput label="Fees (£)" name="fees" type="number" value={form.fees} onChange={handleInputChange} min="0" step="0.01" />
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Availability</label>
                    <Select
                      options={availabilityOptions}
                      value={availabilityOptions.find((opt) => opt.value === form.availability) || null}
                      onChange={handleAvailabilityChange}
                      placeholder="Select availability"
                      isClearable
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Time Slots</label>
                    <Select
                      options={timeSlotOptions}
                      value={timeSlotOptions.filter((opt) => form.timeSlots.includes(opt.value))}
                      onChange={handleTimeSlotsChange}
                      isMulti
                      placeholder="Select available time slots"
                    />
                  </div>
                  <FormInput label="Experience (Years)" name="experience" type="number" value={form.experience} onChange={handleInputChange} min="0" max="50" />
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Specialties</label>
                    <Select
                      options={specialtyOptions}
                      value={specialtyOptions.filter((opt) => form.specialties.includes(opt.value))}
                      onChange={handleSpecialtyChange}
                      isMulti
                      placeholder="Select up to 5 specialties"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Bio</label>
                    <textarea
                      name="bio"
                      value={form.bio}
                      onChange={handleInputChange}
                      placeholder="Enter bio or about yourself"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white"
                      rows="5"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-8">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setForm((prev) => ({ ...prev, profilePicture: null }));
                }}
                className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-white font-semibold rounded-lg shadow-md hover:bg-secondary"
              >
                Update Profile
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Password Modal - unchanged */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        className="max-w-lg mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border-2 border-primary dark:border-secondary"
      >
        {/* ... password form remains the same ... */}
      </Modal>

      {/* Profile Picture View Modal - unchanged */}
      <Modal
        isOpen={isProfilePictureModalOpen}
        onClose={() => {
          if (selectedProfilePicture?.blobUrl) URL.revokeObjectURL(selectedProfilePicture.blobUrl);
          setIsProfilePictureModalOpen(false);
          setSelectedProfilePicture(null);
          setDocumentError(null);
        }}
        title="View Profile Picture"
        className="max-w-3xl mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border-2 border-primary dark:border-secondary"
      >
        {/* ... picture view remains the same ... */}
      </Modal>
    </div>
  );
}

export default UserProfile;